import { useState } from 'react';
import { HelpModal } from '../components/HelpModal';
import { SettingsModal } from '../components/SettingsModal';

/** Main menu. The marquee/tagline are unchanged; below the Play button sit
 * two secondary entry points (How to Play, Settings) opened as modals so
 * neither one navigates away from this screen -- `onPlay` is still the
 * only thing that moves to create/join. */
export function Landing({ onPlay }: { onPlay: () => void }): JSX.Element {
  const [modal, setModal] = useState<'help' | 'settings' | null>(null);

  return (
    <div className="screen landing-screen" data-testid="screen-landing">
      <div className="marquee">
        <h1 className="marquee-title">
          <span className="marquee-pac" style={{ color: '#FFDC3C' }}>
            PAC
          </span>
          {/* DEFECT 6 fix: no space between the two spans (by design -- the
              title reads as one word "PACMATES"), which also means there is
              NO permitted line-break point between them -- with the real
              'Press Start 2P' webfont applied (considerably wider than the
              Courier New/monospace fallback shown before it loads), the
              title + marquee padding no longer fit a 375px viewport, and
              since there's nowhere to wrap, the box was forced wider than
              the viewport (genuine overflow, not truncation). `<wbr />`
              inserts a real line-break OPPORTUNITY with zero visual/spacing
              effect at any width that still fits -- it only ever activates
              at the exact narrow widths where wrapping is the only way to
              stay inside the viewport. */}
          <wbr />
          <span className="marquee-mates" style={{ color: '#39FF88' }}>
            MATES
          </span>
        </h1>
        <p className="marquee-tag">Co-op Pac-Man with a friend. Same maze, same dots, two Pac-Men, real time.</p>
      </div>
      <div className="menu-actions">
        <button type="button" className="btn btn-primary btn-lg" onClick={onPlay} autoFocus>
          Play
        </button>
        <div className="menu-actions-secondary">
          <button type="button" className="btn btn-secondary" onClick={() => setModal('help')}>
            How to Play
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setModal('settings')}>
            Settings
          </button>
        </div>
      </div>
      {/* PER-SCREEN 01 fix (ROUND 2): the OS emoji glyphs (key/star/keyboard)
          each brought their own platform-specific gloss/color that clashed
          with the app's flat neon-outline system (a critic named this as a
          visual mismatch, not just "plain text"). Replaced with matching
          1.5px monoline inline SVGs in the maze's own cyan stroke color
          (`--wall-rim`, same hue as the wall rims/panel borders elsewhere)
          -- one consistent icon language instead of 3 unrelated glyph
          systems. Still purely decorative (aria-hidden; the visible text
          next to each icon already carries the full meaning). */}
      <ul className="landing-features">
        <li>
          <span className="landing-feature-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="6" cy="12" r="4" />
              <path d="M10 12 H21" />
              <path d="M17 12 V16" />
              <path d="M21 12 V15.5" />
            </svg>
          </span>
          Share a 6-character team code. No account needed
        </li>
        <li>
          <span className="landing-feature-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2.5 L14.7 9.3 L22 9.9 L16.4 14.6 L18.2 21.5 L12 17.6 L5.8 21.5 L7.6 14.6 L2 9.9 L9.3 9.3 Z" />
            </svg>
          </span>
          Independent lives and scores, shared maze and ghosts
        </li>
        <li>
          <span className="landing-feature-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="6" width="20" height="12" rx="2" />
              <path d="M5.5 10 h1 M9 10 h1 M12.5 10 h1 M16 10 h1 M19 10 h1" />
              <path d="M5.5 13.5 h1 M9 13.5 h1 M12.5 13.5 h6.5" />
            </svg>
          </span>
          Desktop: Arrow keys or WASD. Mobile: on-screen joystick or swipe (D-pad available in Settings)
        </li>
      </ul>

      {modal === 'help' && <HelpModal onClose={() => setModal(null)} />}
      {modal === 'settings' && <SettingsModal onClose={() => setModal(null)} />}
    </div>
  );
}
